import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, none } from 'ramda';
import { isNilOrEmptyString, isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import { getLabelMessage } from '@gef-ui/localization/messages';

import ComponentLoader from 'components/molecules/ComponentLoader';
import fetch from 'features/common/fetch';
import { useGeneratePersonOptions } from 'features/other/organizationStructureMUI/hooks';
import { fetchPPZ } from 'features/other/organizationStructureMUI/ppz/actions';
import { getPPZ, getPpzForSidebar } from 'features/other/organizationStructureMUI/ppz/selectors';

const Ppz = ({
	fieldName,
	formName,
	label,
	disableOnOneOptions,
	autoSelect,
	onChange,
	type = 'form',
	customAllowedPpz = null,
	...props
}) => {
	const dispatch = useDispatch();
	const next = useControlNext(formName);
	const formData = useFormData();
	const formIsInitiated = isObject(formData);

	const ppz = useSelector((state) =>
		type === 'sidebar' ? getPpzForSidebar(state, formData, customAllowedPpz) : getPPZ(state, formData),
	);
	const options = useGeneratePersonOptions(ppz);

	const handleFetchOnSuccess = useCallback(
		(ppzs) => {
			if (autoSelect) {
				if (ppzs.length === 1 && disableOnOneOptions) {
					next(setValue({ fieldName, value: ppzs[0].login }));
					onChange && onChange(null, ppzs[0].login);
				}
			}
		},
		// eslint-disable-next-line
		[autoSelect, disableOnOneOptions, next, fieldName],
	);

	useEffect(() => {
		if (
			formIsInitiated &&
			none(isNilOrEmptyString, [
				formData?.channel,
				formData?.region,
				formData?.agency,
				formData?.manager,
				formData?.seller,
			])
		) {
			dispatch(
				fetchPPZ({
					formName,
					channelId: formData?.channel,
					regionId: formData?.region,
					agencyId: formData?.agency,
					managerLogin: formData?.manager,
					sellerLogin: formData?.seller,
					type,
					onSuccess: handleFetchOnSuccess,
					customAllowedPpz,
				}),
			);
		}
	}, [
		dispatch,
		formIsInitiated,
		formName,
		formData?.channel,
		formData?.region,
		formData?.agency,
		formData?.manager,
		formData?.seller,
		handleFetchOnSuccess,
		type,
		customAllowedPpz,
	]);

	const handleChange = useCallback(
		(e, value) => {
			onChange && onChange(e, value);
		},
		[onChange],
	);

	return (
		<ComponentLoader fetchStorePath={`${fetch.constants.urls.orgStructurePPZ}.${type}`} ignoreErrors>
			<Autocomplete
				size="small"
				fieldName={fieldName}
				label={label || getLabelMessage('organizationStructure:ppz')}
				options={options}
				disabled={isEmpty(options) || (disableOnOneOptions && options.length === 1)}
				labelPropName="name"
				valuePropName="code"
				onChange={handleChange}
				{...props}
			/>
		</ComponentLoader>
	);
};

Ppz.propTypes = {
	autoSelect: PropTypes.bool,
	customAllowedPpz: PropTypes.array,
	disableOnOneOptions: PropTypes.bool,
	fieldName: PropTypes.string,
	formName: PropTypes.string,
	label: PropTypes.string,
	onChange: PropTypes.func,
	type: PropTypes.oneOf(['form', 'sidebar']),
};

export default Ppz;
